// src/pages/ELearning/CommentsPage.tsx — ФИЧА S: Комментарии к тестам
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getTests, getTestComments, addTestComment, deleteTestComment } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2, MessageSquare, Send, Trash2, User } from 'lucide-react';

const CommentsPage: React.FC = () => {
  const { user } = useAuth();
  const [tests, setTests] = useState<any[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTest = searchParams.get('test') || '';

  const setSelectedTest = (testId: string) => {
    if (testId) {
      setSearchParams({ test: testId });
    } else {
      setSearchParams({});
    }
  };
  const [comments, setComments] = useState<any[]>([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingComments, setLoadingComments] = useState(false);
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const data = await getTests({ active: true });
        setTests(Array.isArray(data) ? data : (data.items || []));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    if (!selectedTest) return;
    (async () => {
      setLoadingComments(true);
      try {
        const d = await getTestComments(selectedTest);
        setComments(d.items || []);
      } catch (e) { console.error(e); }
      finally { setLoadingComments(false); }
    })();
  }, [selectedTest]);

  const handlePost = async () => {
    if (!text.trim() || !selectedTest) return;
    setPosting(true);
    try {
      await addTestComment(selectedTest, text.trim());
      setText('');
      const d = await getTestComments(selectedTest);
      setComments(d.items || []);
    } catch (e: any) { alert('Ошибка: ' + (e.response?.data?.error || e.message)); }
    finally { setPosting(false); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Удалить комментарий?')) return;
    try {
      await deleteTestComment(id);
      setComments(comments.filter(c => c._id !== id));
    } catch (e) { alert('Нет прав'); }
  };

  return (
    <>
      <style>{`@keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } } .cm-card { animation: fadeUp 0.4s ease-out; }`}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <div style={{ width: 56, height: 56, borderRadius: 18, background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 12px 32px rgba(245,158,11,0.4)' }}>
          <MessageSquare size={28} color="#fff" />
        </div>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0, background: 'linear-gradient(135deg, #1a1a2e 0%, #f59e0b 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Комментарии</h1>
          <p style={{ color: '#8b8fa8', margin: '4px 0 0', fontSize: 14, fontWeight: 500 }}>Обсуждение тестов с командой</p>
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <select value={selectedTest} onChange={e => setSelectedTest(e.target.value)} style={{ width: '100%', padding: '14px 18px', borderRadius: 16, border: '1px solid rgba(0,0,0,0.08)', background: 'rgba(255,255,255,0.7)', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          <option value="">Выберите тест...</option>
          {tests.map(t => <option key={t._id} value={t._id}>{t.title}</option>)}
        </select>
      </div>

      {!selectedTest ? (
        <div style={{ textAlign: 'center', padding: 60, background: 'rgba(255,255,255,0.5)', borderRadius: 24, border: '1px dashed rgba(102,126,234,0.3)' }}>
          <MessageSquare size={48} color="#667eea" style={{ opacity: 0.4 }} />
          <h3 style={{ color: '#5a5f7a', marginTop: 12, fontWeight: 700 }}>Выберите тест</h3>
          <p style={{ color: '#8b8fa8', fontSize: 13 }}>Чтобы увидеть комментарии</p>
        </div>
      ) : (
        <>
          {/* Форма нового комментария */}
          <div style={{ background: 'rgba(255,255,255,0.7)', backdropFilter: 'blur(16px)', borderRadius: 20, padding: 16, marginBottom: 16 }}>
            <textarea
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Написать комментарий..."
              rows={3}
              style={{ width: '100%', padding: 14, borderRadius: 14, border: '1px solid rgba(0,0,0,0.08)', fontSize: 14, fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box', outline: 'none' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
              <button onClick={handlePost} disabled={posting || !text.trim()} style={{ padding: '10px 20px', background: text.trim() && !posting ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#95a5a6', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 700, cursor: posting ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, boxShadow: '0 6px 16px rgba(102,126,234,0.3)' }}>
                <Send size={16} /> {posting ? '...' : 'Отправить'}
              </button>
            </div>
          </div>

          {/* Список комментариев */}
          {loadingComments ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Loader2 size={28} color="#667eea" style={{ animation: 'spin 1s linear infinite' }} /></div>
          ) : comments.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#8b8fa8', fontSize: 14 }}>Пока нет комментариев — будьте первым!</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {comments.map((c, i) => (
                <div key={c._id} className="cm-card" style={{ background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(16px)', borderRadius: 18, padding: 16, boxShadow: '0 4px 16px rgba(31,38,135,0.05)', animationDelay: `${i * 0.03}s` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 14 }}>
                      {(c.userName || '?').slice(0, 1).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: '#1a1a2e' }}>{c.userName || 'Пользователь'}</div>
                      <div style={{ fontSize: 11, color: '#8b8fa8' }}>{new Date(c.createdAt).toLocaleString('ru-RU')}</div>
                    </div>
                    {(String(c.userId) === String(user?.id) || ['admin', 'superadmin', 'hr_manager'].includes(user?.role || '')) && (
                      <button onClick={() => handleDelete(c._id)} style={{ padding: 6, background: 'transparent', border: 'none', cursor: 'pointer', color: '#ef4444', borderRadius: 8 }}><Trash2 size={14} /></button>
                    )}
                  </div>
                  <div style={{ fontSize: 14, color: '#1a1a2e', lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{c.text}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
};

export default CommentsPage;
